import { useEffect, useState } from 'react';
import { CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Input, Space, Spin, Tooltip, Typography } from 'antd';
import { useNotificationContext } from '@/features/notifications';
import {
  OrganizationMembershipResponse,
  useCreateShareKey,
  useGetOrganizationMemberships,
} from '@/shared/api';
import { useRouterContext } from '@/shared/router';
import { Col } from '@/shared/ui';
import { OrganizationRemoveShareableLinkModal } from './OrganizationRemoveShareableLinkModal';
import { TabKeys, useTabsContext } from './OrganizationTabs';

const { Title, Text } = Typography;

export const OrganizationShareableLink = () => {
  const { showNotification } = useNotificationContext();
  const { activeOrganization } = useRouterContext();
  const { activeTab } = useTabsContext();
  const [linkCopied, setLinkCopied] = useState(false);
  const [removeLinkModalVisible, setRemoveLinkModalVisible] = useState(false);

  const {
    data: organizationData,
    isLoading: organizationDataLoading,
    refetch: refetchOrganization,
  } = useGetOrganizationMemberships<OrganizationMembershipResponse>(
    { pathParams: { id: activeOrganization?.id as string } },
    { enabled: Boolean(activeOrganization?.id) },
  );

  const { mutate: createShareKey, isLoading: createShareKeyLoading } =
    useCreateShareKey();

  const generateShareLink = () => {
    createShareKey(
      { pathParams: { id: activeOrganization?.id as string } },
      {
        onSuccess: () => {
          refetchOrganization();
          showNotification({
            message: 'Success',
            description: 'Shareable link generated',
            type: 'success',
          });
        },
        onError: (error) => {
          showNotification({
            message: 'Error',
            description: error?.message || 'Something went wrong',
            type: 'error',
          });
        },
      },
    );
  };

  const handleLinkCopy = () => {
    if (organizationData?.shareKey) {
      setLinkCopied(true);
      navigator.clipboard.writeText(
        `${import.meta.env.VITE_BASE_URL}/discover/${organizationData.shareKey}`,
      );
      showNotification({
        message: 'Copied',
        description: 'Link copied to clipboard',
        type: 'success',
      });
    }
  };

  useEffect(() => {
    if (activeTab !== TabKeys.PublicPage) {
      setLinkCopied(false);
    }
  }, [activeTab]);

  if (organizationDataLoading || !organizationData) {
    return <Spin size="small" />;
  }

  if (!organizationData.shareKey) {
    return (
      <Col alignItems="flex-start">
        <Title level={4}>Share organization procs</Title>
        <Text type="secondary" style={{ marginBottom: 30 }}>
          Using this link, users will be able to access all published procs from
          this organization.
        </Text>
        <Button
          type="primary"
          loading={createShareKeyLoading}
          onClick={generateShareLink}
        >
          Generate procs share link
        </Button>
      </Col>
    );
  }

  return (
    <>
      <Col alignItems="flex-start">
        <Title level={4}>Organization procs share link</Title>
        <Text type="secondary" style={{ marginBottom: 10 }}>
          Using this link, users will be able to access all published procs from
          this organization.
        </Text>
        <Space.Compact
          style={{ maxWidth: '100%', marginBottom: 30, width: 580 }}
        >
          <Input
            value={`${import.meta.env.VITE_BASE_URL}/discover/${
              organizationData.shareKey
            }`}
            readOnly
          />
          <Tooltip title={linkCopied ? 'Link copied' : 'Copy link'}>
            <Button
              icon={<CopyOutlined />}
              disabled={linkCopied}
              onClick={handleLinkCopy}
            />
          </Tooltip>
        </Space.Compact>
        <Button
          icon={<DeleteOutlined />}
          type="primary"
          danger
          onClick={() => setRemoveLinkModalVisible(true)}
        >
          Remove shareable link
        </Button>
      </Col>
      <OrganizationRemoveShareableLinkModal
        open={removeLinkModalVisible}
        refetchOrganization={refetchOrganization}
        onSuccess={() => {
          setRemoveLinkModalVisible(false);
          setLinkCopied(false);
        }}
        onCancel={() => setRemoveLinkModalVisible(false)}
      />
    </>
  );
};
